import { useState } from 'react';
import { useMutation, useQuery } from '@apollo/client';
import { useLocation, useNavigate } from 'react-router-dom';
import { Formik } from 'formik';
import Button from '../components/Button';
import { Field, FieldInput, FieldLabel } from '../components/Field';
import Global from '../components/Global';
import Loader from '../components/Loader';
import { Modal, ModalExit, ModalName, ModalPart } from '../components/Modal';
import { useToast } from '../components/Toast';
import { SEND_LOGIN_CODE, VERIFY_LOGIN_CODE } from '../utilities/mutations';
import { VIEWER } from '../utilities/queries';
import { getHome } from '../utilities/getHome';
import client from '../utilities/client';

const LoginModal = () => {
  const [isEmailModalOpen, setIsEmailModalOpen] = useState(
    localStorage.getItem('token') ? false : true
  );
  const [isCodeModalOpen, setIsCodeModalOpen] = useState(false);
  const [isAuthenticated, setIsAuthenticated] = useState(
    localStorage.getItem('token') ? true : false
  );
  const [email, setEmail] = useState();
  const [sendCode] = useMutation(SEND_LOGIN_CODE);
  const [verifyCode] = useMutation(VERIFY_LOGIN_CODE);
  const { loading, data } = useQuery(VIEWER, {
    skip: !localStorage.getItem('token'),
  });
  const location = useLocation();
  const navigate = useNavigate();
  const toast = useToast();

  const openEmailModal = () => {
    setIsCodeModalOpen(false);
    setIsEmailModalOpen(true);
  };

  const openCodeModal = () => {
    setIsEmailModalOpen(false);
    setIsCodeModalOpen(true);
  };

  const openAuthentication = () => {
    setIsEmailModalOpen(false);
    setIsCodeModalOpen(false);
    setIsAuthenticated(true);
  };

  const onClose = () => {
    if (location.state?.inApp) {
      navigate(-1);
    } else {
      getHome(navigate);
    }
  };

  const SpaceRedirectBroker = () => {
    if (data) {
      if (data.viewer.spaces.length !== 0) {
        if (
          data.viewer.spaces.filter(
            (space) => space.slug === localStorage.getItem('lastSpaceSlug')
          ).length !== 0
        ) {
          navigate(`/s/${localStorage.getItem('lastSpaceSlug')}`, {
            replace: true,
          });
        } else {
          if (localStorage.getItem('lastSpaceSlug')) {
            localStorage.removeItem('lastSpaceSlug');
          }

          navigate(`/s/${data.viewer.spaces[0].slug}`, {
            replace: true,
          });
        }
      } else {
        if (localStorage.getItem('lastSpaceSlug')) {
          localStorage.removeItem('lastSpaceSlug');
        }

        navigate('/s/new', { replace: true });
      }

      return <Loader />;
    }
  };

  return (
    <>
      <Global />
      {isEmailModalOpen && (
        <Modal onClose={() => onClose()}>
          <ModalExit />
          <ModalName>Welcome</ModalName>
          <Formik
            initialValues={{ email: '' }}
            onSubmit={async (values) => {
              try {
                await sendCode({ variables: { email: values.email } });
              } catch (error) {
                toast({ children: error.message });

                return;
              }

              setEmail(values.email);
              openCodeModal();
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <ModalPart>
                  <Field>
                    <FieldLabel>Email</FieldLabel>
                    <FieldInput
                      type="email"
                      name="email"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.email}
                      data-autofocus
                    />
                  </Field>
                </ModalPart>
                <ModalPart>
                  <Button
                    type="submit"
                    disabled={values.email === '' || isSubmitting}
                    isPrimary
                    isExtended
                  >
                    Next
                  </Button>
                </ModalPart>
              </form>
            )}
          </Formik>
        </Modal>
      )}
      {isCodeModalOpen && (
        <Modal onClose={() => onClose()}>
          <ModalExit />
          <ModalName>Email sent</ModalName>
          <Formik
            initialValues={{ code: '' }}
            onSubmit={async (values) => {
              try {
                const token = await verifyCode({
                  variables: {
                    code: values.code,
                    email: email,
                  },
                });

                localStorage.setItem('token', token.data.verifyLoginCode);
              } catch (error) {
                toast({ children: error.message });

                return;
              }

              if (location.state?.from?.pathname) {
                navigate(location.state?.from?.pathname, {
                  replace: true,
                });
              } else {
                openAuthentication();
              }

              client.resetStore();
            }}
          >
            {({
              values,
              handleChange,
              handleBlur,
              handleSubmit,
              isSubmitting,
            }) => (
              <form onSubmit={handleSubmit}>
                <ModalPart>
                  <Field>
                    <FieldLabel>Code</FieldLabel>
                    <FieldInput
                      type="text"
                      name="code"
                      onChange={handleChange}
                      onBlur={handleBlur}
                      value={values.code}
                      data-autofocus
                    />
                  </Field>
                </ModalPart>
                <ModalPart>
                  <Button
                    type="submit"
                    disabled={values.code == '' || isSubmitting}
                    isPrimary
                    isExtended
                  >
                    Next
                  </Button>
                  <Button onClick={() => openEmailModal()} isExtended>
                    Use a different email
                  </Button>
                </ModalPart>
              </form>
            )}
          </Formik>
        </Modal>
      )}
      {isAuthenticated && (
        <Modal>
          <ModalPart>
            {loading && !data ? <Loader /> : <SpaceRedirectBroker />}
          </ModalPart>
        </Modal>
      )}
    </>
  );
};

export default LoginModal;
